import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseApiService, ToastService } from '@packages/ng-shared-services';
import { PROGRAMS_ENDPOINTS } from '@packages/shared-types';
import { ProgramService } from '../program/program.service';

/**
 * API Response interface for programs list
 */
export interface ProgramApiResponse {
  success: boolean;
  status: number;
  data: ProgramsListData;
}

/**
 * Programs list data from API
 */
export interface ProgramsListData {
  total: number;
  ongoing_count: number;
  completed_count: number;
  hold_count: number;
  programs: ProgramApiItem[];
}

/**
 * Program item from API
 */
export interface ProgramApiItem {
  id: number;
  program_name: string;
  prog_start_date: string;
  prog_end_date: string;
  status: number;
  status_label: string;
  application_name: string | null;
  mapping_type: string | null;
  panel_url: string | null;
  uid: string;
  program_code: string;
  user_id: number;
  created_date: string;
  updated_date: string | null;
  manager_name: string;
  manager_email: string;
  sales_type: number | null;
}

/**
 * Programs API Service
 * Handles all API calls related to programs management in SuperAdmin
 *
 * @example
 * ```typescript
 * export class ProgramsListComponent {
 *   private programsApi = inject(ProgramsApiService);
 *
 *   loadPrograms() {
 *     this.programsApi.getPrograms().subscribe({
 *       next: (programs) => this.projects.set(programs),
 *       error: (err) => console.error(err)
 *     });
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ProgramsApiService {
  private api = inject(BaseApiService);
  private toast = inject(ToastService);
  private programService = inject(ProgramService);

  /**
   * Get all programs with counts
   * Endpoint: admin/get-programs
   */
  getPrograms(): Observable<ProgramsListData> {
    return this.api
      .getWithHeaders(PROGRAMS_ENDPOINTS.list, {
        'X-Program-Id': 'master_admin',
      })
      .pipe(
        map((response: any) => {
          const data = response.body || response;
          return (
            data.data || {
              total: 0,
              ongoing_count: 0,
              completed_count: 0,
              hold_count: 0,
              programs: [],
            }
          );
        }),
        catchError(error => {
          const errorMsg = error.error?.message || 'Failed to load programs';
          this.toast.error(errorMsg);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get roles for a specific program
   * Endpoint: user-role/list-roles
   *
   * @param programId - Program ID
   * @param programUid - Program UID for X-Program-Id header
   */
  getProgramRoles(programId: number, programUid: string): Observable<any> {
    return this.api
      .getWithHeaders(
        'user-role/list-roles',
        {
          'X-Program-Id': programUid,
        },
        {
          program_id: '1',
        }
      )
      .pipe(
        map((response: any) => {
          const data = response.body || response;
          return data.data || [];
        }),
        catchError(error => {
          const errorMsg = error.error?.message || 'Failed to load program roles';
          this.toast.error(errorMsg);
          return throwError(() => error);
        })
      );
  }

  /**
   * Create or update a program
   * Endpoint: admin/create-program (for new programs with master token)
   */
  createOrUpdateProgram(programData: any): Observable<any> {
    return this.api
      .postWithHeaders(PROGRAMS_ENDPOINTS.create, programData, {
        'X-Program-Id': 'master_admin',
        'X-Api-Key': 'Test@1234',
      })
      .pipe(
        map((response: any) => response.body || response),
        catchError(error => {
          const errorMsg = error.error?.message || 'Failed to save program';
          this.toast.error(errorMsg);
          return throwError(() => error);
        })
      );
  }

  /**
   * Update an existing program
   * Endpoint: admin/update-program
   * Uses program UID as X-Program-Id header (not master_admin)
   *
   * @param programData - Program data with program_id, program_name, start_date, end_date, etc.
   * @param programUid - Program UID for X-Program-Id header
   */
  updateProgram(programData: any, programUid: string): Observable<any> {
    return this.api
      .postWithHeaders(PROGRAMS_ENDPOINTS.update, programData, {
        'X-Program-Id': programUid,
      })
      .pipe(
        map((response: any) => response.body || response),
        catchError(error => {
          const errorMsg = error.error?.message || 'Failed to update program';
          this.toast.error(errorMsg);
          return throwError(() => error);
        })
      );
  }

  /**
   * Delete a program
   * Endpoint: programs/delete-programs
   */
  deleteProgram(programId: number): Observable<any> {
    return this.api
      .postWithHeaders(
        PROGRAMS_ENDPOINTS.delete,
        { program_id: programId },
        {
          'X-Program-Id': 'master_admin',
        }
      )
      .pipe(
        map((response: any) => response.body || response),
        catchError(error => {
          const errorMsg = error.error?.message || 'Failed to delete program';
          this.toast.error(errorMsg);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get program configuration
   * Endpoint: programs/get-programs-configuration
   */
  // getProgramConfig(programId: number): Observable<any> {
  //   return this.api
  //     .getWithHeaders(`${PROGRAMS_ENDPOINTS.getConfig}?program_id=${programId}`, {
  //       'X-Program-Id': 'master_admin',
  //     })
  //     .pipe(
  //       map((response: any) => {
  //         const data = response.body || response;
  //         return data.data || data;
  //       }),
  //       catchError(error => {
  //         const errorMsg = error.error?.message || 'Failed to load program configuration';
  //         this.toast.error(errorMsg);
  //         return throwError(() => error);
  //       })
  //     );
  // }

  /**
   * Update program status
   * Endpoint: admin/update-program-status
   * Status values: 1 = active, 2 = completed, 3 = hold
   *
   * @param programId - Numeric ID of the program
   * @param status - Status code (1: active, 2: completed, 3: hold)
   * @param uid - Program UID for X-Program-Id header
   */
  updateProgramStatus(programId: number, status: number, uid: string): Observable<any> {
    return this.api
      .postWithHeaders(
        PROGRAMS_ENDPOINTS.updateStatus,
        {
          program_id: programId,
          status: status,
        },
        {
          'X-Program-Id': 'master_admin',
        }
      )
      .pipe(
        map((response: any) => response.body || response),
        catchError(error => {
          const errorMsg = error.error?.message || 'Failed to update program status';
          this.toast.error(errorMsg);
          return throwError(() => error);
        })
      );
  }

  /**
   * Program-specific login to get program token
   * Endpoint: admin/program-login
   * Called before accessing an existing program's configuration
   *
   * @param programId - Numeric ID of the program (dynamic)
   * @param uid - Program UID for X-Program-Id header
   * @param email - Email (dynamic, from login)
   * @returns Observable with program token
   *
   * Note: Password is hardcoded to "123456" as per API requirement
   */
  programLogin(programId: number, uid: string, email: string): Observable<any> {
    return this.api
      .postWithHeaders(
        PROGRAMS_ENDPOINTS.programLogin,
        {
          program_id: programId, // Dynamic - selected program
          email: email, // Dynamic - from login
          password: '123456', // Static - hardcoded
        },
        {
          'X-Program-Id': uid,
        }
      )
      .pipe(
        map((response: any) => response.body || response),
        catchError(error => {
          const errorMsg = error.error?.message || 'Failed to login to program';
          this.toast.error(errorMsg);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get app information / configurations
   * Endpoint: app-content/get-app-info
   */
  getAppInfo(): Observable<any> {
    const programId = this.programService.programId();
    const headers = {
      'X-Program-Id': this.programService.programUid(),
    };

    const url = `${PROGRAMS_ENDPOINTS.getAppInfo}?program_id=1`;
    return this.api.getWithHeaders(url, headers).pipe(
      map((response: any) => {
        const data = response.body || response;
        return data.data || data || [];
      }),
      catchError(error => {
        const errorMsg = error.error?.message || 'Failed to load app information';
        this.toast.error(errorMsg);
        return throwError(() => error);
      })
    );
  }

  /**
   * Fetch a single app configuration by ID
   * Endpoint: app-content/get-app-info-by-id
   */
  getAppInfoById(id: string | number): Observable<any> {
    const headers = {
      'X-Program-Id': this.programService.programUid(),
    };

    const url = `${PROGRAMS_ENDPOINTS.getAppInfo}?id=${id}`;
    return this.api.getWithHeaders(url, headers).pipe(
      map((response: any) => {
        const data = response.body || response;
        return data.data || data || {};
      }),
      catchError(error => {
        const errorMsg = error.error?.message || 'Failed to load app configuration';
        this.toast.error(errorMsg);
        return throwError(() => error);
      })
    );
  }

  getAppInfoLogs(): Observable<any> {
    const programId = this.programService.programId();
    const headers = {
      'X-Program-Id': this.programService.programUid(),
    };

    const url = `${PROGRAMS_ENDPOINTS.applog}?program_id=1`;
    return this.api.getWithHeaders(url, headers).pipe(
      map((response: any) => {
        const data = response.body || response;
        return data.data || data || [];
      }),
      catchError(error => {
        const errorMsg = error.error?.message || 'Failed to load app information';
        this.toast.error(errorMsg);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create app information (supports file uploads via FormData)
   * Endpoint: app-content/create-app-info
   */
  createAppInfo(payload): Observable<any> {
    const headers = {
      'X-Program-Id': this.programService.programUid(),
    };
    return this.api.postWithFormData(PROGRAMS_ENDPOINTS.createAppInfo, payload, headers).pipe(
      map((response: any) => response.body || response),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Update existing app information (supports file uploads via FormData)
   * Endpoint: app-content/update-app-info
   */
  updateAppInfo(id: string | number, payload: FormData): Observable<any> {
    const headers = {
      'X-Program-Id': this.programService.programUid(),
    };

    const endpoint = `${PROGRAMS_ENDPOINTS.updateAppInfo}?id=${id}`;
    return this.api.postWithFormData(endpoint, payload, headers).pipe(
      map((response: any) => response.body || response),
      catchError(error => {
        const errorMsg = error.error?.message || 'Failed to update app information';
        this.toast.error(errorMsg);
        return throwError(() => error);
      })
    );
  }
  generateApk(id: number): Observable<any> {
    const headers = {
      'X-Program-Id': this.programService.programUid(),
    };
    const payload = {
      log_id: id,
    };
    const endpoint = `${PROGRAMS_ENDPOINTS.generateApk}`;
    return this.api.postWithHeaders(endpoint, payload, headers).pipe(
      map((response: any) => response.body || response),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  requestApk(id: number): Observable<any> {
    const headers = {
      'X-Program-Id': this.programService.programUid(),
    };
    const payload = {
      id: id,
    };
    const endpoint = `${PROGRAMS_ENDPOINTS.requestApk}`;
    return this.api.postWithHeaders(endpoint, payload, headers).pipe(
      map((response: any) => response.body || response),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }
}
